import moment from 'moment';

/*
 * Tooltip for one point on the DG utilisation chart.
 * ---------------------------------------------------
 * A point is one full-tank-to-full-tank interval. Hovering it shows everything the
 * Intervals table holds for that interval AND the working behind each derived
 * figure, so a reader can check the number without leaving the chart:
 *
 *   Interval   — opening and closing full tank, with their vouchers
 *   Measured   — hours run, KWH generated, fuel consumed (and how many entries)
 *   Calculated — operating level, kWh/L, L/hour, rated kW, utilisation,
 *                each shown with the division that produced it
 *
 * Every value is read from the interval row built by buildAssetIntervals — nothing
 * is recomputed here that could disagree with the table.
 *
 * SECURITY: ECharts renders the returned string as HTML. Asset codes, voucher
 * numbers and exclusion reasons come from the API, so every string is escaped
 * before it is placed in the markup.
 */

export const escapeHtml = (value) =>
  String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

const fmt = (v, dp) =>
  isNum(v) ? v.toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp }) : '—';

const when = (ms) => (isNum(ms) ? moment(ms).format('DD-MMM-YYYY HH:mm') : '—');

const MUTED = 'color:#8c8c8c';

const section = (title) =>
  `<tr><td colspan="3" style="padding-top:6px;font-weight:600;color:#262626;border-top:1px solid #f0f0f0">${title}</td></tr>`;

// label and working are built from escaped or numeric parts only.
const row = (label, value, working = '') =>
  `<tr>` +
  `<td style="${MUTED};padding-right:12px">${label}</td>` +
  `<td style="text-align:right;font-weight:600;white-space:nowrap">${value}</td>` +
  `<td style="${MUTED};padding-left:10px;white-space:nowrap">${working}</td>` +
  `</tr>`;

const voucher = (no) => (no ? `voucher ${escapeHtml(no)}` : '<i>no voucher</i>');

/**
 * @param {Object} interval a row from buildAssetIntervals
 * @param {number} powerFactor the power factor the report is using
 * @returns {string} tooltip HTML, or '' when there is no interval
 */
export const buildIntervalTooltipHtml = (interval, powerFactor) => {
  if (!interval) return '';
  const iv = interval;

  const kwh = `${fmt(iv.kwhRun, 2)} kWh`;
  const hours = `${fmt(iv.hoursRun, 2)} h`;
  const litres = `${fmt(iv.fuelL, 2)} L`;
  const ratedKw = isNum(iv.ratedKva) && isNum(powerFactor) ? iv.ratedKva * powerFactor : null;
  const elapsedHours = isNum(iv.currentMs) && isNum(iv.previousMs) ? (iv.currentMs - iv.previousMs) / 3600000 : null;

  let fuelMakeUp = '';
  if (isNum(iv.fuelEntryCount)) {
    const topUps = iv.fuelEntryCount - 1;
    fuelMakeUp = topUps === 0 ? 'closing full tank only' : `closing full tank + ${topUps} top-up${topUps === 1 ? '' : 's'}`;
  }

  const fuelCurve = iv.validForFuelCurve
    ? '<span style="color:#389e0d">Included in the fleet fuel curve</span>'
    : `<span style="color:#d46b08">Not in the fleet fuel curve — ${escapeHtml(iv.fuelCurveReason || 'no reason recorded')}</span>`;

  return (
    `<div style="font-size:12px;line-height:1.6">` +
    `<div style="font-weight:600;font-size:13px;margin-bottom:2px">${escapeHtml(iv.assetCode)}</div>` +
    `<table style="border-collapse:collapse">` +
    section('Interval') +
    row('From', when(iv.previousMs), voucher(iv.openingVoucherNo)) +
    row('To', when(iv.currentMs), voucher(iv.voucherNo)) +
    row('Elapsed', `${fmt(elapsedHours, 1)} h`, 'calendar time between the two full tanks') +
    section('Measured') +
    row('Hours run', hours, 'hour meter') +
    row('KWH generated', kwh, 'KWH meter') +
    row('Fuel consumed', litres, fuelMakeUp) +
    section('Calculated') +
    row('Operating level', `${fmt(iv.avgKw, 2)} kW`, `${kwh} ÷ ${hours}`) +
    row('kWh/L', fmt(iv.efficiencyKwhPerL, 2), `${kwh} ÷ ${litres}`) +
    row('L/hour', fmt(iv.litresPerHour, 2), `${litres} ÷ ${hours}`) +
    row('Rated output', `${fmt(ratedKw, 1)} kW`, `${fmt(iv.ratedKva, 1)} kVA × ${fmt(powerFactor, 2)} PF`) +
    row(
      'Utilisation',
      isNum(iv.utilisation) ? `${(iv.utilisation * 100).toFixed(1)}%` : '—',
      `${fmt(iv.avgKw, 2)} kW ÷ ${fmt(ratedKw, 1)} kW`
    ) +
    `</table>` +
    `<div style="margin-top:6px">${fuelCurve}</div>` +
    `</div>`
  );
};
